import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { NuxxumLogo, NuxxumWordmark } from "@/components/NuxxumLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "h-11 bg-white/[0.05] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#766EFF]/60 focus-visible:ring-offset-0";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#171927] text-white overflow-x-hidden flex flex-col" data-testid="forgot-password-page">
      <div className="pointer-events-none fixed inset-0 opacity-60"
        style={{ background: "radial-gradient(ellipse at 15% 10%, #5B52EB26 0%, transparent 45%), radial-gradient(ellipse at 85% 90%, #7AD7C414 0%, transparent 45%)" }} />
      <header className="relative max-w-7xl w-full mx-auto px-6 lg:px-10 h-20 flex items-center gap-3">
        <NuxxumLogo size={38} />
        <NuxxumWordmark className="text-2xl text-white" />
      </header>
      <main className="relative flex-1 flex items-start justify-center px-6 pt-8 pb-16">
        <div className="w-full max-w-md">
          <button
            data-testid="forgot-back-btn"
            onClick={() => navigate("/auth")}
            className="flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Volver a iniciar sesión
          </button>
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-7 sm:p-8 space-y-6 shadow-2xl shadow-black/40">
            {sent ? (
              <div className="space-y-4 text-center" data-testid="forgot-sent">
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </div>
                <h2 className="font-heading text-2xl font-bold">Revisa tu correo</h2>
                <p className="text-sm text-white/60 leading-relaxed">
                  Si existe una cuenta con <strong className="text-white">{email}</strong>, te acabamos de enviar un enlace para restablecer tu contraseña. El enlace caduca en 60 minutos.
                </p>
                <p className="text-xs text-white/40">
                  ¿No ves el correo? Revisa la carpeta de spam o promociones.
                </p>
                <Button
                  onClick={() => navigate("/auth")}
                  data-testid="forgot-back-login-btn"
                  className="w-full h-11 bg-gradient-to-r from-[#5B52EB] to-[#766EFF] hover:opacity-90 transition-opacity border-0 text-white"
                >
                  Volver a iniciar sesión
                </Button>
              </div>
            ) : (
              <>
                <div>
                  <div className="h-12 w-12 rounded-xl bg-[#5B52EB]/15 flex items-center justify-center mb-4">
                    <Mail className="h-5 w-5 text-[#9A94FF]" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold tracking-tight">¿Olvidaste tu contraseña?</h2>
                  <p className="text-white/50 mt-1 text-sm">
                    Introduce tu correo y te enviaremos un enlace seguro para elegir una nueva.
                  </p>
                </div>
                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="forgot-email" className="text-white/70 text-xs">Correo electrónico</Label>
                    <Input
                      id="forgot-email"
                      data-testid="forgot-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@correo.com"
                      required
                      className={inputCls}
                    />
                  </div>
                  {error && <p className="text-sm text-red-400" data-testid="forgot-error">{error}</p>}
                  <Button
                    type="submit"
                    data-testid="forgot-submit-btn"
                    disabled={loading}
                    className="w-full h-11 text-sm font-semibold bg-gradient-to-r from-[#5B52EB] to-[#766EFF] hover:opacity-90 transition-opacity border-0 text-white"
                  >
                    {loading ? "Enviando…" : "Enviar enlace de restablecimiento"}
                  </Button>
                </form>
                <p className="text-xs text-white/40 text-center">
                  ¿Recordaste la contraseña?{" "}
                  <Link to="/auth" className="text-[#9A94FF] hover:underline">Inicia sesión</Link>
                </p>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
